import { IAutomationDenormalized, IExperienceMessageDenormalized, IExperienceTemplate, ITaskDenormalized } from '@dadohr/shared/domainTypes/experience';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@material-ui/core';
import { AlertCircle, PlayCircle } from 'lucide-react';
import React, { useState } from 'react';
import { ButtonWithoutPropagation } from '../../../../../components/ButtonWithoutPropagation';
import { ButtonWithPromise } from '../../../../../components/ButtonWithPromise';
import { FlexContainer } from '../../../../../layout/Flex';
import { joinWithCommaAndAnd } from '../../../../../services/enumerate';
import { useProductAnalytics } from '../../../../../services/productAnalytics';

interface ForceTriggerButtonProps {
  item: ITaskDenormalized | IExperienceMessageDenormalized | IAutomationDenormalized;
  itemType: 'task' | 'message' | 'automation';
  triggeredBy: ITaskDenormalized[];
  isUntriggered: boolean;
  template: IExperienceTemplate,
  onForceTrigger?: () => Promise<void>;
  enqueueSnackbarSuccess: (message: string) => void;
  enqueueSnackbarError: (message: string) => void;
  t: (key: string) => string;
}

export const ForceTriggerButton: React.FC<ForceTriggerButtonProps> = ({
  item,
  itemType,
  triggeredBy,
  isUntriggered,
  template,
  onForceTrigger,
  enqueueSnackbarSuccess,
  enqueueSnackbarError,
  t
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { trackEvent } = useProductAnalytics();

  const itemName = itemType === 'message' ? (item as IExperienceMessageDenormalized).subject : (item as ITaskDenormalized | IAutomationDenormalized).title,
        triggerTitles = joinWithCommaAndAnd(triggeredBy.flatMap(x => x.title), t),
        executionName = itemType === 'automation' ? 'run' : itemType === 'message' ? 'send' : 'start';

  // Mixpanel tracking
  const trackTrigger = () => {
    trackEvent({
      eventType: PAEventType.EXPERIENCE_INSTANCE,
      name: 'Effect Manually Triggered',
      Explanation: 'An Admin manually triggered an effect before the cause had completed',
      Subdomain: 'app.dadohr',
      'Experience ID': template.id,
      'Experience Name': template.adminTitle || template.title,
      'Timeline Item ID': (itemType === 'message' ? item.templateId : (item as ITaskDenormalized | IAutomationDenormalized).id).replace('-triggered', ''),
      'Timeline Item Type': itemType,
      'Timeline Item Title': itemName,
      'Instance ID': triggeredBy[0].experienceId,
      'Cause ID': joinWithCommaAndAnd(triggeredBy.flatMap(x => x.templateId), t),
      'Cause Status': joinWithCommaAndAnd(triggeredBy.flatMap(x => x.status), t),
      'Cause Title': triggerTitles
    })
  }

  const handleConfirmedTrigger = async () => {
    setIsLoading(true);
    try {
      await onForceTrigger?.();
      enqueueSnackbarSuccess('This item has been started successfully');
      
      try {
        trackTrigger();
      } catch (error: any) {
        console.error('Mixpanel tracking failed:', error);
      }
      
      setShowConfirmDialog(false);
    } catch (error: any) {
      console.error('Force trigger failed:', error);
      enqueueSnackbarError(error?.message ? error.message : 'Force trigger failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isUntriggered || !onForceTrigger) {
    return null;
  }

  

  return (
    <>
      <ButtonWithoutPropagation
        variant="outlined"
        size="small"
        onClick={async () => setShowConfirmDialog(true)}
        startIcon={<PlayCircle size={16} />}
      >
        {t(`${executionName.charAt(0).toUpperCase() + executionName.slice(1)} now`)}
      </ButtonWithoutPropagation>
      
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <FlexContainer justifyContent="start" spacing={1}>
            <AlertCircle />
            {`Confirm triggering ${itemName}`}
          </FlexContainer>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" component="p">
            {t(`This ${itemType} is usually triggered by the completion of the task `)}<strong>{triggerTitles}</strong>{t('.')}
          </Typography>
          <Typography variant="body2" component="p" style={{marginTop: 12}}>
            {t(`Please confirm you'd like this ${itemType} to ${executionName} now, regardless of whether `)}
            {<strong>{triggerTitles}</strong>}
            {t(` is completed or not.`)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowConfirmDialog(false)} 
            color="default"
            disabled={isLoading}
          >
            {t('Cancel')}
          </Button>
          <ButtonWithPromise
            onClick={handleConfirmedTrigger}
            pending={t('Starting...')}
            color="primary"
            variant="contained"
          >
            {t(`${executionName.charAt(0).toUpperCase() + executionName.slice(1)} now`)}
          </ButtonWithPromise>
        </DialogActions>
      </Dialog>
    </>
  );
};